import type { SoilRound, ProgrammeProductLine, ProgrammeGroup } from '../../services/nutritionService';

// Editor-local working shapes (mirror the service types but allow string
// inputs while the user is typing — converted on save).
export interface EditableProductLine {
  productId: string;
  productName?: string;
  unit: string;
  doseBasis: 'perTree' | 'per100L';
  value: string;
}
export interface EditableGroup {
  groupId: string;
  label: string;
  orchardIds: string[];
  products: EditableProductLine[];
}
export interface EditableRound {
  roundId: string;
  type: 'soil' | 'foliar';
  name: string;
  month: string;
  /**
   * The <input type="date"> value, 'YYYY-MM-DD', or '' when unset.
   *
   * This field exists because it was MISSING: roundSchema has carried
   * plannedStart since the model was written, but the editable shape did not,
   * so buildRounds never emitted it and the whole-programme PUT (which
   * replaces doc.rounds outright) deleted it from every round on every save.
   * nutritionOrderService and foliarBoardSummary both read it.
   */
  plannedStart: string;
  waterRatePerHa: string;
  groups: EditableGroup[];
}
export interface EditableYoungTree {
  name: string;
  products: EditableProductLine[];
}

/**
 * Pure transformation for ONE round. buildRounds is this, mapped — there is
 * exactly one place where the trim/Number/filter rules live.
 *
 * plannedStart is emitted as UTC MIDNIGHT. Never round-trip it through
 * `new Date(value).toISOString()` from a local-midnight Date: in SAST (+02:00)
 * that serialises to the previous day at 22:00 and the date walks backwards one
 * day per save. UTC midnight is already the convention the CSV descriptor uses
 * (dataTransfer/descriptors/nutritionProgram.js parses a bare YYYY-MM-DD, which
 * JS defines as UTC).
 */
export function buildRound(r: EditableRound): SoilRound {
  const groups: ProgrammeGroup[] = r.groups
    .map((g) => ({
      groupId: g.groupId,
      label: g.label.trim() || undefined,
      orchardIds: g.orchardIds,
      products: g.products
        .map((p): ProgrammeProductLine => ({
          productId: p.productId.trim(),
          productName: p.productName?.trim() || undefined,
          unit: p.unit,
          doseBasis: p.doseBasis ?? 'perTree',
          value: Number(p.value),
        }))
        .filter((p) => p.productId !== '' && Number.isFinite(p.value) && p.value > 0),
    }))
    .filter((g) => g.orchardIds.length > 0 || g.products.length > 0);
  return {
    roundId: r.roundId,
    type: r.type ?? 'soil',
    name: r.name.trim(),
    ...(r.month.trim() ? { month: Number(r.month) } : {}),
    ...(r.plannedStart.trim() ? { plannedStart: `${r.plannedStart.trim()}T00:00:00.000Z` } : {}),
    ...(r.waterRatePerHa.trim() ? { waterRatePerHa: Number(r.waterRatePerHa) } : {}),
    groups,
  };
}

/**
 * Pure transformation: build the `rounds` payload from the editor's
 * working shapes. Each round owns its `groups[]`; every group carries its
 * orchards and products. Products carry a single numeric `value`. Drops
 * products with blank/non-positive value and drops groups left with neither
 * orchards nor products. Includes `month` / `waterRatePerHa` as Numbers only
 * when non-empty. The per-round transformation rules live in `buildRound`.
 */
export function buildRounds(rounds: EditableRound[]): SoilRound[] {
  return rounds.map(buildRound);
}

/**
 * The <input type="date"> value for a stored plannedStart.
 *
 * Takes the first ten characters of the stored ISO string. Do NOT use
 * `new Date(iso).toISOString().slice(0,10)`: a value stored at local midnight
 * in SAST (+02:00) serialises to the PREVIOUS day at 22:00, so the date walks
 * backwards one day on every save.
 */
export function plannedStartInputValue(iso: string | null | undefined): string {
  return iso ? String(iso).slice(0, 10) : '';
}

/**
 * Pure transformation: build the `youngTreeProgramme` payload. A flat product
 * list (no groups/orchards). Products use the same trim/Number/filter rules as
 * buildRounds. Returns `undefined` when absent (null) or when there is neither a
 * name nor a surviving product — so we never persist an empty `{}`. A name with
 * no products is still persisted (name-only sub-programme is valid).
 */
export function buildYoungTree(
  yt: EditableYoungTree | null,
): { name?: string; products: ProgrammeProductLine[] } | undefined {
  if (!yt) return undefined;
  const name = yt.name.trim();
  const products: ProgrammeProductLine[] = yt.products
    .map((p): ProgrammeProductLine => ({
      productId: p.productId.trim(),
      productName: p.productName?.trim() || undefined,
      unit: p.unit,
      doseBasis: p.doseBasis ?? 'perTree',
      value: Number(p.value),
    }))
    .filter((p) => p.productId !== '' && Number.isFinite(p.value) && p.value > 0);
  if (!name && products.length === 0) return undefined;
  return { ...(name ? { name } : {}), products };
}
